import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent {
  email = '';
  password = '';
  userType = '';
  loading = false;
  error = '';
  successMessage = '';
  submitted = false;
  showPassword = false;
  
  // Field-specific error messages
  userTypeError = '';
  emailError = '';
  passwordError = '';

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  selectRole(role: string) {
    this.userType = role;
    this.clearError();
  }

  showError(message: string) {
    this.error = message;
    this.successMessage = ''; // Clear success message when showing error
    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (this.error === message) {
        this.error = '';
      }
    }, 5000);
  }

  showSuccess(message: string) {
    this.successMessage = message;
    this.error = ''; // Clear error message when showing success
    // Auto-hide after 3 seconds
    setTimeout(() => {
      if (this.successMessage === message) {
        this.successMessage = '';
      }
    }, 3000);
  }

  clearError() {
    // Clear error message when user starts interacting with form fields
    if (this.error) {
      this.error = '';
    }
    // Clear field-specific errors
    this.userTypeError = '';
    this.emailError = '';
    this.passwordError = '';
  }


  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  onSubmit() {
    this.submitted = true;
    
    // Clear previous field errors
    this.userTypeError = '';
    this.emailError = '';
    this.passwordError = '';
    
    let hasError = false;
    
    if (!this.userType) {
      this.userTypeError = 'Please select a user type';
      this.showError('Please select a user type');
      hasError = true;
    }

    if (!this.email) {
      this.emailError = 'Email is required';
      if (!hasError) {
        this.showError('Email is required');
        hasError = true;
      }
    }

    if (!this.password) {
      this.passwordError = 'Password is required';
      if (!hasError) {
        this.showError('Password is required');
        hasError = true;
      }
    }
    
    if (hasError) {
      return;
    }

    this.loading = true;
    this.error = '';
    
    this.authService.login(this.email, this.password).subscribe({
      next: (response) => {
        // console.log('Login response:', response);
        // Verify user type matches selected role
        if (response && response.user && response.user.userType !== this.userType) {
          this.loading = false;
          this.showError(`This account is registered as ${response.user.userType}, not ${this.userType}`);
          return;
        }
        // Show success message
        this.loading = false;
        this.showSuccess('Login successful!');
        
        // Redirect after a short delay to show success message
        setTimeout(() => {
          const user = response.user;
          if (user.userType === 'Supplier') {
            // Use replaceUrl to prevent going back to login page
            this.router.navigate(['/leads'], { replaceUrl: true });
          } else {
            // Admin/Manager - Use replaceUrl to prevent going back to login page
            this.router.navigate(['/product'], { replaceUrl: true });
          }
        }, 10);
      },
      error: (err) => {
        console.error('Login error:', err);
        this.loading = false;
        let errorMessage = '';
        if (err.status === 0) {
          errorMessage = 'Cannot connect to server. Please make sure the backend API is running on http://localhost:3000';
        } else if (err.status === 401) {
          errorMessage = err.error?.message || 'Invalid email or password';
        } else if (err.status === 404) {
          errorMessage = 'API endpoint not found. Please check if the backend is running.';
        } else {
          errorMessage = err.error?.message || err.message || 'Login failed. Please try again.';
        }
        this.showError(errorMessage);
      }
    });
  }
}

